import type { AutocompleteDeleteEvent } from '@ingenico/react-ui/form-section';
import { isString, toString } from 'lodash-es';
import { useCallback } from 'react';
import type { SmartFieldCategoryOption, SmartFieldsResource } from '../../../domain/models/smartFields.model';

export function useParameterModuleSmartFieldSearchContent(
  handleCategorySearchChange: (value: string) => void,
  handleSmartfieldSearchChange: (value: string) => void,
  selected: SmartFieldsResource | undefined,
) {
  const handleCategoryInputChange = useCallback(
    (_: unknown, value: string, reason: 'clear' | 'input' | 'reset') => {
      if (reason === 'input' || reason === 'clear') {
        handleCategorySearchChange?.(value);
      }
    },
    [handleCategorySearchChange],
  );

  const handleSmartfieldInputChange = useCallback(
    (_: unknown, value: string, reason: 'clear' | 'input' | 'reset') => {
      // Only update search on actual input or clear, ignore reset (handled by onBlur)
      if (reason === 'input' || reason === 'clear') {
        handleSmartfieldSearchChange?.(value);
      }
    },
    [handleSmartfieldSearchChange],
  );

  const handleSmartfieldBlur = useCallback(() => {
    // On blur: only reset search if no item is selected
    // If an item is selected, keep the search query to avoid triggering unnecessary API calls
    if (!selected) {
      handleSmartfieldSearchChange?.('');
    }
  }, [handleSmartfieldSearchChange, selected]);

  const handleCategoryDelete = useCallback(
    (
      event: AutocompleteDeleteEvent,
      categories: SmartFieldCategoryOption[],
      setCategories: (value: SmartFieldCategoryOption[]) => void,
    ) => {
      const indexToBeRemoved = event.index;
      const newValue = categories.filter((_, index: number) => index !== indexToBeRemoved);
      setCategories(newValue);
    },
    [],
  );

  const getOptionLabel = useCallback((option: SmartFieldCategoryOption | string) => {
    if (isString(option)) {
      return option;
    }
    return toString(option?.label);
  }, []);

  return {
    handleCategoryInputChange,
    handleSmartfieldInputChange,
    handleSmartfieldBlur,
    getOptionLabel,
    handleCategoryDelete,
  };
}
